/**
 * Premium Shipping Protection Auto-Add System
 * Automatically manages shipping protection based on cart value
 */

class ShippingProtection {
  constructor() {
    this.productId = 8799359074473;
    this.protectionEnabled = this.getProtectionState();
    this.isProcessing = false;
    this.lastRequestTime = 0;
    this.REQUEST_COOLDOWN = 2000; // Reduced from 5000ms to 2000ms
    this.hasFixedQuantity = false; // Flag to prevent infinite loops
    this.hasAutoAdded = false; // Flag to prevent auto-add after manual toggle
    this.lastProtectionTier = this.getLastProtectionTier(); // Track last tier to detect changes
    this.cartCache = null; // Cache cart data to avoid redundant API calls
    this.cacheTimeout = 2000; // Cache valid for 2 seconds
    this.variantIds = [
      46555234762921, 46555234795689, 46555234828457, 46555234861225, 46555234893993,
    46555234926761, 46555234959529, 46555234992297, 46555235025065, 46555235057833,
    46555235090601, 46555235123369, 46555235156137, 46555235188905, 46555235221673,
    46555235254441, 46555235287209, 46555235319977, 46555235352745, 46555235385513,
    46555235418281, 46555235451049, 46555235483817, 46555235516585, 46555235549353,
    46555235582121, 46555235614889, 46555235647657, 46555235680425, 46555235713193,
    46555235745961, 46555235778729, 46555235811497, 46555235844265, 46555235877033,
    46555235909801, 46555235942569, 46555235975337, 46555236008105, 46555236040873,
    46555236073641, 46555236106409, 46555236139177, 46555236171945, 46555236204713,
    46555236237481, 46555236270249, 46555236303017, 46555236335785, 46555236368553,
    46555236401321, 46555236434089, 46555236466857, 46555236499625, 46555236532393,
    46555236565161, 46555236597929, 46555236630697, 46555236663465, 46555236696233,
    46555236729001, 46555236761769, 46555236794537, 46555236827305, 46555236860073,
    46555236892841, 46555236925609, 46555236958377, 46555236991145, 46555237023913,
    46555237056681, 46555237089449, 46555237122217, 46555237154985, 46555237187753,
    46555237220521, 46555237253289, 46555237286057, 46555237318825, 46555237351593,
    46555237384361, 46555237417129, 46555237449897, 46555237482665, 46555237515433,
    46555237548201, 46555237580969, 46555237613737, 46555237646505, 46555237679273,
    46555237712041, 46555237744809, 46555237777577, 46555237810345, 46555237843113,
    46555237875881, 46555237908649, 46555237941417, 46555237974185, 46555238006953
    ];
    
    this.init();
  }

  init() {
    // Only operate on cart page
    if (window.location.pathname !== '/cart') {
      return;
    }

    // Listen for cart updates
    document.addEventListener('DOMContentLoaded', () => {
      this.showProtectionSection();
      this.setupCheckboxHandler();
      this.updateProtectionDisplay();
      // Auto-add protection on page load if enabled - do this immediately
      this.autoAddProtectionOnLoad();
    });
  }

  async getCartData(useCache = true) {
    // Use cached data if available and recent
    if (useCache && this.cartCache && (Date.now() - this.cartCache.timestamp) < this.cacheTimeout) {
      return this.cartCache.data;
    }

    // Fetch fresh cart data
    const response = await fetch('/cart.js');
    const cart = await response.json();
    
    // Cache the result
    this.cartCache = {
      data: cart,
      timestamp: Date.now()
    };

    return cart;
  }

  async getCartSubtotal(useCache = true) {
    // Get cart subtotal excluding shipping protection
    const cart = await this.getCartData(useCache);
    let subtotal = 0;
    cart.items.forEach(item => {
      if (item.product_id !== this.productId) {
        subtotal += item.line_price;
      }
    });
    return subtotal;
  }

  async getCurrentProtectionItem(useCache = true) {
    const cart = await this.getCartData(useCache);
    
    const protectionItems = cart.items.filter(item => item.product_id === this.productId);
    
    if (protectionItems.length > 1) {
      this.removeDuplicateProtection(protectionItems);
      return protectionItems[0]; // Return the first one
    }
    
    const protectionItem = protectionItems[0] || null;
    return protectionItem;
  }

  calculateRequiredTier(subtotal) {
    // Each tier covers $1000, starting from $0
    // Tier 1: $0-1000 = $30, Tier 2: $1001-2000 = $60, etc.
    const tier = Math.min(Math.ceil(subtotal / 100000), 100); // Convert cents to dollars and divide by 1000
    return tier;
  }

  getVariantIdForTier(tier) {
    // Tiers are 1-indexed, array is 0-indexed
    return this.variantIds[tier - 1];
  }

  getProtectionState() {
    // Default to enabled (true) unless explicitly disabled
    return localStorage.getItem('shipping_protection_enabled') !== 'false';
  }

  setProtectionState(enabled) {
    this.protectionEnabled = enabled;
    localStorage.setItem('shipping_protection_enabled', enabled.toString());
  }

  getLastProtectionTier() {
    return parseInt(localStorage.getItem('last_protection_tier')) || 0;
  }

  setLastProtectionTier(tier) {
    this.lastProtectionTier = tier;
    localStorage.setItem('last_protection_tier', tier.toString());
  }

  async updateProtection() {
    // Rate limiting check
    const now = Date.now();
    if (this.isProcessing || (now - this.lastRequestTime) < this.REQUEST_COOLDOWN) {
      return;
    }

    this.isProcessing = true;
    this.lastRequestTime = now;

    try {
      if (!this.protectionEnabled) {
        await this.removeProtection();
        return;
      }

      const subtotal = await this.getCartSubtotal();
      const currentProtection = await this.getCurrentProtectionItem();
      const requiredTier = this.calculateRequiredTier(subtotal);
      
      if (subtotal === 0) {
        // Empty cart, remove protection
        if (currentProtection) {
          await this.removeProtection();
        }
        return;
      }

      const requiredVariantId = this.getVariantIdForTier(requiredTier);

      if (!currentProtection) {
        // No protection in cart, add it
        await this.addProtection(requiredVariantId);
      } else if (currentProtection.variant_id !== requiredVariantId) {
        // Wrong tier, update it
        await this.updateProtectionTier(currentProtection, requiredVariantId);
      }
    } finally {
      // Reset processing flag after cooldown period
      setTimeout(() => {
        this.isProcessing = false;
      }, this.REQUEST_COOLDOWN / 2); // Reset flag sooner than full cooldown
    }
  }

  async addProtection(variantId, shouldReload = false) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });
      
      if (response.status === 429) {
        setTimeout(() => this.addProtection(variantId, shouldReload), 3000);
        return;
      }
      
      if (response.ok) {
        // Invalidate cache since cart changed
        this.cartCache = null;
        
        if (shouldReload) {
          setTimeout(() => window.location.reload(), 300);
        } else {
          // Update displays immediately with reduced delay
          setTimeout(() => this.updateProtectionDisplay(), 200);
        }
      }
    } catch (error) {
      // Silent error handling
    }
  }

  async removeProtection() {
    const currentProtection = await this.getCurrentProtectionItem();
    if (currentProtection) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: currentProtection.key,
            quantity: 0
          })
        });
        
        if (response.ok) {
          // Invalidate cache since cart changed
          this.cartCache = null;
          return true;
        } else {
          return false;
        }
      } catch (error) {
        return false;
      }
    } else {
      return true;
    }
  }

  async updateProtectionTier(currentProtection, newVariantId, shouldReload = false) {
    // Remove current protection and add new tier
    await this.removeProtection();
    await this.addProtection(newVariantId, shouldReload);
  }

  setupCheckboxHandler() {
    const checkbox = document.getElementById('shipping-protection-toggle');
    if (checkbox) {
      checkbox.checked = this.protectionEnabled;
      checkbox.addEventListener('change', (e) => {
        this.setProtectionState(e.target.checked);
        
        if (e.target.checked) {
          this.manualUpdateProtection().then(() => {
            setTimeout(() => window.location.reload(), 300);
          });
        } else {
          this.removeProtection().then((success) => {
            if (success) {
              setTimeout(() => window.location.reload(), 300);
            } else {
              e.target.checked = true;
              this.setProtectionState(true);
            }
          });
        }
      });
    }
  }

  observeCartChanges() {
    // Listen for cart form submissions
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('submit', () => {
        // Delay to allow cart update to process
        setTimeout(() => this.updateProtection(), 300);
      });
    }
  }



  async removeDuplicateProtection(protectionItems) {
    for (let i = 1; i < protectionItems.length; i++) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: protectionItems[i].key,
            quantity: 0
          })
        });
      } catch (error) {
        // Silent error handling
      }
    }
  }

  async fixProtectionQuantity(protectionItem) {
    // Fix protection quantity to always be 1
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: protectionItem.key,
          quantity: 1
        })
      });
      
      if (response.ok) {
        console.log('Protection quantity fixed successfully');
        // Refresh to show the updated quantity
        setTimeout(() => window.location.reload(), 500);
      } else {
        console.error('Failed to fix protection quantity:', response.status);
      }
    } catch (error) {
      console.error('Error fixing protection quantity:', error);
    }
  }

  showProtectionSection() {
    const section = document.getElementById('shipping-protection-section');
    if (section) {
      section.style.display = 'block';
    }
  }


  async updateProtectionDisplay() {
  const subtotal = await this.getCartSubtotal(); // in cents
  const tier = this.calculateRequiredTier(subtotal);

  const coveredValueDollars = tier * 1000; // <-- key change: use tier coverage blocks

  let price;
  if (tier <= 10) {
    price = coveredValueDollars * 0.03; // 3% of covered value
  } else if (tier <= 15) {
    price = 300; // flat
  } else {
    price = coveredValueDollars * 0.02; // 2% of covered value
  }

  // Optional: round to whole dollars for display
  const displayPrice = Math.round(price);

  const priceDisplay = document.getElementById('protection-price-display');
  if (priceDisplay) {
    priceDisplay.textContent = `Cost: $${displayPrice}.00`;
  }

  await this.updateFallbackProtectionDisplay();
}


  async updateFallbackProtectionDisplay() {
    const subtotal = await this.getCartSubtotal();
    const currentProtection = await this.getCurrentProtectionItem();
    const fallbackElement = document.getElementById('protection-price-fallback');
    const priceAmountElement = document.getElementById('protection-price-amount');
    
    if (fallbackElement && priceAmountElement) {
      if (this.protectionEnabled && currentProtection && currentProtection.line_price > 0 && subtotal > 0) {
        // Show fallback with current protection price
        const priceFormatted = this.formatMoney(currentProtection.line_price);
        priceAmountElement.textContent = priceFormatted + ' CAD';
        fallbackElement.style.display = 'flex';
      } else {
        // Hide fallback
        fallbackElement.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  async autoAddProtectionOnLoad() {
    if (!this.protectionEnabled) {
      this.setLastProtectionTier(0);
      return;
    }

    const subtotal = await this.getCartSubtotal();
    const currentProtection = await this.getCurrentProtectionItem();
    const requiredTier = this.calculateRequiredTier(subtotal);
    
    if (subtotal === 0) {
      this.setLastProtectionTier(0);
      return;
    }

    const tierChanged = this.lastProtectionTier !== requiredTier;

    if (currentProtection) {
      const requiredVariantId = this.getVariantIdForTier(requiredTier);
      
      if (currentProtection.quantity !== 1 && !this.hasFixedQuantity) {
        this.hasFixedQuantity = true;
        await this.fixProtectionQuantity(currentProtection);
        return;
      }
      
      if (currentProtection.variant_id === requiredVariantId) {
        this.setLastProtectionTier(requiredTier);
        return;
      } else {
        this.setLastProtectionTier(requiredTier);
        await this.updateProtectionTier(currentProtection, requiredVariantId, true);
        return;
      }
    }

    const requiredVariantId = this.getVariantIdForTier(requiredTier);
    const shouldReload = tierChanged && this.lastProtectionTier !== 0;
    this.setLastProtectionTier(requiredTier);
    this.hasAutoAdded = true;
    await this.addProtection(requiredVariantId, shouldReload);
  }

  async manualUpdateProtection() {
    try {
      const subtotal = await this.getCartSubtotal();
      const currentProtection = await this.getCurrentProtectionItem();
      const requiredTier = this.calculateRequiredTier(subtotal);
      
      if (subtotal === 0) return;

      const requiredVariantId = this.getVariantIdForTier(requiredTier);

      if (!currentProtection) {
        await this.addProtection(requiredVariantId);
      } else if (currentProtection.variant_id !== requiredVariantId) {
        await this.updateProtectionTier(currentProtection, requiredVariantId);
      }
    } catch (error) {
      // Silent error handling
    }
  }
}

// Initialize shipping protection
window.shippingProtection = new ShippingProtection();